import { Grid, TextField } from '@mui/material';
import { FC, useRef } from 'react';
import FormField from '@acorn-portal/shared/src/components/inputs/FormField';
import { useFormikContext } from 'formik';
import { isEmptyObject } from '@acorn-portal/shared/src/utils';

export interface ILicenceField {
	'licenceField-1': string;
	'licenceField-2': string;
	'licenceField-3': string;
	'licenceField-4': string;
}

interface IForm {
	[key: string]: ILicenceField;
}

interface IState {
	id?: string;
	name?: string;
	partialLock?: boolean; // only affects fields 1 and 2
}

const DriverLicenceField: FC<IState> = ({ id = 'licenceNumber', name = '', partialLock }) => {
	const ref = useRef<HTMLDivElement>(null);
	const formik = useFormikContext<IForm>();

	const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
		const { maxLength, value, name } = event.target;
		const [, fieldIndex] = name.split('-');
		formik.setFieldValue(`${id}.${name}`, value.toLocaleUpperCase(), true);

		const fieldIntIndex = parseInt(fieldIndex, 10);

		if (value.length === maxLength && ref.current) {
			if (fieldIntIndex < 4) {
				const nextField = ref.current.querySelector<HTMLInputElement>(`input[name=licenceField-${fieldIntIndex + 1}]`);

				if (nextField !== null) {
					nextField.focus();
				}
			}
		}
	};

	const onFocus = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>) => {
		if (!ref.current) return;
		const currentField = ref.current.querySelector<HTMLInputElement>(`input[name=${event.target.name}]`);
		if (currentField) {
			currentField.type = 'text';
		}
	};

	const GetHelperText = () => {
		if (
			typeof formik.errors[id] === 'object' &&
			formik.touched[id]?.['licenceField-1'] &&
			!isEmptyObject(formik.errors[id] as object)
		) {
			return 'Driver licence number is required';
		}

		if (typeof formik.errors[id] === 'string' && Boolean(formik.errors[id])) {
			return formik.errors[id] as string;
		}
		return undefined;
	};

	return (
		<FormField
			label='Driving Licence number'
			name={name}
			error={!!GetHelperText() || (formik.touched[id]?.['licenceField-1'] && Boolean(formik.errors[id]))}
			helperText={GetHelperText()}
		>
			<Grid
				ref={ref}
				container
				direction='row'
				spacing={1}
				sx={(theme) => ({
					'& input': {
						[theme.breakpoints.down('md')]: {
							px: theme.spacing(1),
						},
					},
				})}
			>
				<Grid size={{ xs: 3.5 }}>
					<TextField
						autoComplete='off'
						fullWidth
						name='licenceField-1'
						type='text'
						onFocus={onFocus}
						slotProps={{
							htmlInput: {
								maxLength: 5,
								autoComplete: 'off',
								spellCheck: 'false',
								autoCorrect: 'off',
							},
						}}
						onChange={(event) => {
							handleChange(event);
						}}
						value={formik.values[id]['licenceField-1']}
						error={
							formik.touched[id]?.['licenceField-1'] &&
							(Boolean(formik.errors[id]?.['licenceField-1']) || Boolean(formik.errors[id]))
						}
					/>
				</Grid>
				<Grid size={{ xs: 3.5 }}>
					<TextField
						fullWidth
						name='licenceField-2'
						type='text'
						onFocus={onFocus}
						slotProps={{
							htmlInput: {
								maxLength: 6,
								autoComplete: 'off',
								spellCheck: 'false',
								autoCorrect: 'off',
								readOnly: partialLock === true,
							},
						}}
						onChange={(event) => {
							handleChange(event);
						}}
						value={formik.values[id]['licenceField-2']}
						error={
							formik.touched[id]?.['licenceField-2'] &&
							(Boolean(formik.errors[id]?.['licenceField-2']) || Boolean(formik.errors[id]))
						}
					/>
				</Grid>

				<Grid size={3}>
					<TextField
						fullWidth
						name='licenceField-3'
						type='text'
						onFocus={onFocus}
						slotProps={{
							htmlInput: {
								maxLength: 3,
								autoComplete: 'off',
								spellCheck: 'false',
								autoCorrect: 'off',
							},
						}}
						onChange={(event) => {
							handleChange(event);
						}}
						value={formik.values[id]['licenceField-3']}
						error={
							formik.touched[id]?.['licenceField-3'] &&
							(Boolean(formik.errors[id]?.['licenceField-3']) || Boolean(formik.errors[id]))
						}
					/>
				</Grid>

				<Grid size={2}>
					<TextField
						fullWidth
						name='licenceField-4'
						type='text'
						onFocus={onFocus}
						slotProps={{
							htmlInput: {
								maxLength: 2,
								autoComplete: 'off',
								spellCheck: 'false',
								autoCorrect: 'off',
							},
						}}
						onChange={(event) => {
							handleChange(event);
						}}
						value={formik.values[id]['licenceField-4']}
						error={
							formik.touched[id]?.['licenceField-4'] &&
							(Boolean(formik.errors[id]?.['licenceField-4']) || Boolean(formik.errors[id]))
						}
					/>
				</Grid>
			</Grid>
		</FormField>
	);
};

export default DriverLicenceField;
